import { useEffect, useState } from "react";
import { ShieldOff, ShieldCheck, ScrollText, X } from "lucide-react";

export const REASONS = [
  ["documents_pending", "Documents Pending"],
  ["suspended", "Suspended"],
  ["excess_leave", "Excess Leave"],
  ["other", "Other"],
];

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-3 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

export const HoldBadge = ({ badge, email }) => badge ? (
  <span className="text-[10px] tracking-[0.12em] uppercase font-semibold border border-red-500/60 bg-red-500/15 text-red-300 px-2 py-0.5 whitespace-normal" data-testid={`team-hold-badge-${email}`}>{badge}</span>
) : null;

export const DocStatusChip = ({ member, canEdit, onToggle }) => {
  const pending = member.doc_status === "pending";
  return (
    <button type="button" disabled={!canEdit} onClick={() => onToggle(member, pending ? "verified" : "pending")}
      title={canEdit ? "Click to toggle document verification status" : ""}
      className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-0.5 ${pending ? "border-amber-500/50 text-amber-300" : "border-emerald-500/40 text-emerald-300"} ${canEdit ? "hover:border-white/40" : "cursor-default"}`}
      data-testid={`team-doc-status-${member.email}`}>
      Docs: {pending ? "Pending" : "Verified"}
    </button>
  );
};

export const DisableAccessModal = ({ member, onClose, onConfirm, busy }) => {
  const [reason, setReason] = useState("documents_pending");
  const [text, setText] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="disable-access-modal">
      <div className="w-full max-w-md border border-red-500/40 bg-[#0b1220] p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-red-300 flex items-center gap-2"><ShieldOff size={13} /> Staff Access Control</p>
            <h3 className="text-white font-semibold mt-1 break-words">Disable all access — {member.name}</h3>
            <p className="text-xs text-slate-400 mt-1 break-all">{member.email}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white shrink-0" data-testid="disable-access-close"><X size={18} /></button>
        </div>
        <label className="block text-[11px] tracking-[0.14em] uppercase text-slate-500 mb-1.5">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className={input} data-testid="disable-access-reason">
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {reason === "other" && (
          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={400} rows={3} placeholder="Describe the reason (shown in the HR email and the log)" className={`${input} mt-3`} data-testid="disable-access-text" />
        )}
        <p className="text-xs text-slate-400 mt-4 leading-relaxed">
          Instantly blocks NexSecure Teams (this portal), NexSecure Connect, ERPNext, company email, Telegram bots and biometric punches; all live sessions are signed out.{" "}
          {reason === "other"
            ? <span className="text-amber-300" data-testid="disable-access-silent-note">Silent: no email or message is sent to the staff member — the action is only recorded in the Access Control Log.</span>
            : "An HR Department email goes to the personal email on record."}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mt-5">
          <button onClick={onClose} className="border border-white/15 text-slate-300 hover:text-white px-4 py-2.5 text-xs tracking-[0.1em] uppercase" data-testid="disable-access-cancel">Cancel</button>
          <button disabled={busy || (reason === "other" && !text.trim())} onClick={() => onConfirm(reason, text.trim())}
            className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-semibold px-4 py-2.5 text-xs tracking-[0.1em] uppercase" data-testid="disable-access-confirm">
            {busy ? "Disabling…" : "Disable All Access"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AccessButtons = ({ member, onDisable, onRestore }) => member.hold_badge ? (
  <button onClick={() => onRestore(member)} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/50 hover:border-emerald-400 px-3 py-1.5 transition-colors shrink-0" data-testid={`team-restore-access-${member.email}`}>
    <ShieldCheck size={12} /> Restore All Access
  </button>
) : (
  <button onClick={() => onDisable(member)} className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-red-300 border border-red-500/40 hover:border-red-400 px-3 py-1.5 transition-colors shrink-0" data-testid={`team-disable-access-${member.email}`}>
    <ShieldOff size={12} /> Disable All Access
  </button>
);

const fmtTime = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const AccessControlLog = ({ api, refreshKey }) => {
  const [logs, setLogs] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/team/access-log").then((r) => setLogs(r.data.logs || [])).catch(() => setLogs([])); }, [api, refreshKey]);
  if (!logs) return null;
  return (
    <div className="border border-white/10 bg-white/[0.02]" data-testid="access-control-log">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 text-left" data-testid="access-control-log-toggle">
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2 min-w-0"><ScrollText size={13} className="text-nx-goldlight shrink-0" /> <span>Staff Access Control Log ({logs.length}) · CEO only</span></p>
        <span className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 flex flex-col gap-2">
          {logs.length === 0 && <p className="text-xs text-slate-500">No access control actions yet.</p>}
          {logs.map((l, i) => (
            <div key={`${l.at}-${i}`} className="border border-white/10 px-3 py-2.5 text-xs flex flex-col gap-1" data-testid={`access-log-row-${i}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className={`font-semibold tracking-[0.1em] uppercase ${l.action === "disable" ? "text-red-300" : "text-emerald-300"}`}>{l.action === "disable" ? "Disabled" : "Restored"}</span>
                <span className="text-white font-medium">{l.name || l.email}</span>
                <span className="text-slate-500 break-all">{l.email}</span>
                <span className="text-slate-400 ml-auto">{fmtTime(l.at)}</span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-400">
                {l.reason_label && <span>Reason: <span className="text-slate-200">{l.reason_label}</span></span>}
                <span>By: <span className="text-slate-200">{l.by_name || l.by}</span></span>
                <span>Email: <span className={l.email_status === "sent" ? "text-emerald-300" : l.email_status === "failed" ? "text-red-300" : "text-amber-300"}>{l.email_status}{l.email_to ? ` → ${l.email_to}` : ""}{l.email_detail ? ` (${l.email_detail})` : ""}</span></span>
                {l.systems?.erpnext && <span>ERPNext: <span className="text-slate-200">{l.systems.erpnext}</span></span>}
                {l.systems?.webmail && <span>Email: <span className="text-slate-200">{l.systems.webmail}</span></span>}
                {l.systems?.telegram && <span>Telegram: <span className="text-slate-200">{l.systems.telegram}</span></span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
